import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";

import { ApiError, apiGet, apiPost, apiPutFile } from "./api";
import type { Account, Job, MachinePublicStatus } from "./types";

const MAX_BYTES = 75 * 1024 * 1024;
const MIN_DURATION_SECONDS = 3;
const MAX_DURATION_SECONDS = 10;

type AccessState = "loading" | "required" | "allowed";

function money(minor: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(minor / 100);
}

function useAccount(access: AccessState) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (access !== "allowed") return;
    setLoading(true);
    try {
      const result = await apiGet<{ account: Account | null }>("/api/me");
      setAccount(result.account);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [access]);

  return { account, loading, refresh, setAccount };
}

export function App() {
  const location = useLocation();
  const [access, setAccess] = useState<AccessState>("loading");

  useEffect(() => {
    apiGet<{ allowed: boolean }>("/api/access")
      .then(({ allowed }) => setAccess(allowed ? "allowed" : "required"))
      .catch(() => setAccess("required"));
  }, []);

  const publicMachinePage = location.pathname.replace(/\/+$/u, "") === "/machine";

  if (publicMachinePage) {
    return <VendingApp access={access} />;
  }

  if (access === "loading") {
    return <LoadingScreen label="Powering up the machine" />;
  }

  if (access === "required") {
    return <AccessGate onAllowed={() => setAccess("allowed")} />;
  }

  return <VendingApp access={access} />;
}

function VendingApp({ access }: { access: AccessState }) {
  const accountState = useAccount(access);

  return (
    <div className="site-shell">
      <Header account={accountState.account} />
      <main>
        <Routes>
          <Route path="/" element={<Studio account={accountState.account} />} />
          <Route
            path="/account"
            element={
              <AccountPage
                account={accountState.account}
                loading={accountState.loading}
                refresh={accountState.refresh}
              />
            }
          />
          <Route path="/jobs/:jobId" element={<JobPage />} />
          <Route path="/machine" element={<MachinePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function Header({ account }: { account: Account | null }) {
  return (
    <header className="site-header">
      <Link to="/" className="wordmark" aria-label="One Change home">
        <span className="wordmark-dot" />
        ONE CHANGE
      </Link>
      <nav aria-label="Primary navigation">
        <Link to="/machine">How it runs</Link>
        <Link to="/account" className="account-link">
          {account ? `${money(account.balanceMinor)} credit` : "Account"}
        </Link>
      </nav>
    </header>
  );
}

function AccessGate({ onAllowed }: { onAllowed: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await apiPost("/api/access", { code });
      onAllowed();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That code did not work.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="gate-page">
      <section className="gate-machine" aria-labelledby="gate-title">
        <div className="gate-display">
          <span className="status-light" /> TEST MACHINE / PRIVATE RUN
        </div>
        <div className="gate-body">
          <p className="eyebrow">One prompt in. One video out.</p>
          <h1 id="gate-title">CHANGE<br />ONE THING.</h1>
          <p>
            This machine uses paid generation behind test-card checkout. Enter the
            prototype code to keep its real API spend bounded.
          </p>
          <form onSubmit={submit} className="gate-form">
            <label htmlFor="access-code">Prototype access code</label>
            <div className="inline-field">
              <input
                id="access-code"
                type="password"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
              />
              <button type="submit" disabled={busy || !code}>
                {busy ? "Checking" : "Enter"}
              </button>
            </div>
            {error ? <p className="form-error" role="alert">{error}</p> : null}
          </form>
        </div>
        <div className="gate-slot"><span /></div>
      </section>
    </main>
  );
}

function Studio({ account }: { account: Account | null }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState<"16:9" | "9:16">("16:9");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<"guest" | "credits" | null>(null);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const chooseFile = async (candidate: File) => {
    setError("");
    if (candidate.size > MAX_BYTES) {
      setError("Use an MP4 smaller than 75 MB.");
      return;
    }
    if (candidate.type !== "video/mp4" && !candidate.name.toLowerCase().endsWith(".mp4")) {
      setError("This machine currently accepts MP4 video only.");
      return;
    }

    const url = URL.createObjectURL(candidate);
    try {
      const seconds = await readVideoDuration(url);
      if (seconds < MIN_DURATION_SECONDS || seconds > MAX_DURATION_SECONDS) {
        throw new Error("Choose a clip between 3 and 10 seconds.");
      }
      if (preview) URL.revokeObjectURL(preview);
      setFile(candidate);
      setDuration(seconds);
      setPreview(url);
    } catch (cause) {
      URL.revokeObjectURL(url);
      setError(cause instanceof Error ? cause.message : "That video could not be read.");
    }
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    const candidate = event.target.files?.[0];
    if (candidate) void chooseFile(candidate);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const candidate = event.dataTransfer.files[0];
    if (candidate) void chooseFile(candidate);
  };

  const purchase = async (method: "guest" | "credits") => {
    if (!file || duration === null) {
      setError("Add a video first.");
      return;
    }
    const cleanPrompt = prompt.trim();
    if (cleanPrompt.length < 3) {
      setError("Describe the change you want.");
      return;
    }
    if (method === "credits" && !account) {
      navigate("/account?return=/");
      return;
    }

    setBusy(method);
    setError("");
    try {
      const draft = await apiPost<{ jobId: string; uploadUrl: string }>("/api/jobs", {
        fileName: file.name,
        contentType: "video/mp4",
        sizeBytes: file.size,
        durationMs: Math.round(duration * 1000),
        prompt: cleanPrompt,
        aspectRatio,
        paymentMethod: method,
      });
      await apiPutFile(draft.uploadUrl, file);
      const result = await apiPost<{ checkoutUrl?: string; queued?: boolean }>(
        `/api/jobs/${draft.jobId}/purchase`,
        { method },
      );
      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
        return;
      }
      navigate(`/jobs/${draft.jobId}`);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "insufficient_credits") {
        setError("Your account needs more credits for this edit.");
      } else {
        setError(cause instanceof Error ? cause.message : "The vend could not start.");
      }
      setBusy(null);
    }
  };

  return (
    <>
      <section className="hero">
        <div>
          <p className="eyebrow">A generative video vending machine</p>
          <h1>CHANGE<br /><em>ONE</em> THING.</h1>
        </div>
        <p className="hero-copy">
          Drop in one short clip. Tell it exactly what should change. Get one finished
          transformation—no timeline, layers, or editing software.
        </p>
      </section>

      <section className="vend-grid" aria-label="Create a video edit">
        <div
          className={`upload-bay ${preview ? "has-video" : ""}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
        >
          {preview ? (
            <video src={preview} controls playsInline aria-label="Selected source video" />
          ) : (
            <button type="button" className="drop-prompt" onClick={() => inputRef.current?.click()}>
              <span className="drop-icon">+</span>
              <strong>Drop your MP4 here</strong>
              <small>3–10 seconds · up to 75 MB</small>
            </button>
          )}
          <input
            ref={inputRef}
            className="visually-hidden"
            type="file"
            accept="video/mp4,.mp4"
            onChange={handleInput}
          />
          {file ? (
            <div className="file-strip">
              <span>{file.name}</span>
              <span>{duration?.toFixed(1)}s</span>
              <button type="button" onClick={() => inputRef.current?.click()}>Replace</button>
            </div>
          ) : null}
        </div>

        <div className="instruction-panel">
          <div className="step-label"><span>01</span> The instruction</div>
          <label htmlFor="edit-prompt">What should change?</label>
          <textarea
            id="edit-prompt"
            maxLength={500}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Turn the red umbrella into a cloud. Keep everything else the same."
          />
          <div className="prompt-meta">
            <span>We preserve everything else by default.</span>
            <span>{prompt.length}/500</span>
          </div>

          <fieldset className="ratio-picker">
            <legend>Output frame</legend>
            <button
              type="button"
              className={aspectRatio === "16:9" ? "selected" : ""}
              onClick={() => setAspectRatio("16:9")}
            >
              <span className="ratio landscape" /> Landscape
            </button>
            <button
              type="button"
              className={aspectRatio === "9:16" ? "selected" : ""}
              onClick={() => setAspectRatio("9:16")}
            >
              <span className="ratio portrait" /> Portrait
            </button>
          </fieldset>

          {error ? <p className="form-error" role="alert">{error}</p> : null}

          <div className="purchase-row">
            <button
              type="button"
              className="vend-button primary"
              disabled={busy !== null}
              onClick={() => void purchase("guest")}
            >
              <span>{busy === "guest" ? "Preparing checkout" : "Buy one edit"}</span>
              <strong>$5</strong>
            </button>
            <button
              type="button"
              className="vend-button secondary"
              disabled={busy !== null}
              onClick={() => void purchase("credits")}
            >
              <span>{busy === "credits" ? "Reserving" : account ? "Use account credit" : "Sign in for credit price"}</span>
              <strong>400</strong>
            </button>
          </div>
          <p className="test-note">Stripe test mode · generated audio · one render per vend</p>
        </div>
      </section>

      <section className="truth-strip" aria-label="Product limits">
        <div><span>INPUT</span><strong>One MP4</strong><small>3–10 seconds</small></div>
        <div><span>CHANGE</span><strong>One prompt</strong><small>No masks or timeline</small></div>
        <div><span>OUTPUT</span><strong>One video</strong><small>720p · 24 FPS</small></div>
        <div><span>OVERFLOW</span><strong>For charity</strong><small>After costs and reserve</small></div>
      </section>
    </>
  );
}

function AccountPage({
  account,
  loading,
  refresh,
}: {
  account: Account | null;
  loading: boolean;
  refresh: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const topupSuccess = new URLSearchParams(location.search).get("topup") === "success";

  useEffect(() => {
    if (!topupSuccess) return;
    const timer = window.setInterval(() => void refresh(), 1500);
    const stop = window.setTimeout(() => window.clearInterval(timer), 12000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [topupSuccess]);

  if (loading) return <LoadingScreen label="Reading your account" compact />;

  if (account) {
    const topup = async (pack: "1000" | "2500") => {
      setBusy(true);
      setError("");
      try {
        const { checkoutUrl } = await apiPost<{ checkoutUrl: string }>("/api/wallet/checkout", { pack });
        window.location.assign(checkoutUrl);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Checkout could not start.");
        setBusy(false);
      }
    };

    const logout = async () => {
      await apiPost("/api/auth/logout");
      window.location.assign("/");
    };

    return (
      <section className="account-page">
        <div className="account-heading">
          <p className="eyebrow">One account, every Machine</p>
          <h1>Your credit shelf.</h1>
          <p>{account.email}</p>
        </div>
        <div className="balance-card">
          <span>Available service credit</span>
          <strong>{money(account.balanceMinor)}</strong>
          {account.reservedMinor ? <small>{money(account.reservedMinor)} currently reserved</small> : null}
        </div>
        {topupSuccess ? <p className="notice">Payment received. Credit appears after the signed Stripe webhook arrives.</p> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="pack-grid">
          <button type="button" disabled={busy} onClick={() => void topup("1000")}>
            <span>Ten-dollar pack</span><strong>$10</strong><small>2 complete edits + $2 remaining</small>
          </button>
          <button type="button" disabled={busy} onClick={() => void topup("2500")}>
            <span>Twenty-five-dollar pack</span><strong>$25</strong><small>6 complete edits + $1 remaining</small>
          </button>
        </div>
        <div className="account-actions">
          <Link to="/">Make an edit</Link>
          <button type="button" onClick={() => void logout()}>Sign out</button>
        </div>
        <p className="account-fineprint">
          Credits are first-party, non-transferable service value. They cannot be cashed
          out and do not become Machine revenue until a result is delivered.
        </p>
      </section>
    );
  }

  const authenticate = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await apiPost(`/api/auth/${mode}`, { email, password });
      await refresh();
      const returnTo = new URLSearchParams(location.search).get("return");
      if (returnTo?.startsWith("/")) navigate(returnTo);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Account request failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="auth-layout">
      <div>
        <p className="eyebrow">Optional by design</p>
        <h1>Walk up once.<br />Come back cheaper.</h1>
        <p>
          Guests can always buy one result. An account lets one card payment fund
          multiple vends—and is designed to be shared by future Machines.
        </p>
      </div>
      <form className="auth-card" onSubmit={authenticate}>
        <div className="auth-tabs">
          <button type="button" className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>Sign in</button>
          <button type="button" className={mode === "signup" ? "active" : ""} onClick={() => setMode("signup")}>Create account</button>
        </div>
        <label htmlFor="email">Email</label>
        <input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <label htmlFor="password">Password</label>
        <input id="password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} required />
        {mode === "signup" ? <small>At least 12 characters. Prototype accounts are test-only.</small> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <button className="submit-button" type="submit" disabled={busy}>{busy ? "Working" : mode === "login" ? "Sign in" : "Create account"}</button>
      </form>
    </section>
  );
}

function JobPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!jobId) return;
    let active = true;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const result = await apiGet<{ job: Job }>(`/api/jobs/${jobId}`);
        if (!active) return;
        setJob(result.job);
        if (!["succeeded", "failed", "refunded"].includes(result.job.status)) {
          timer = window.setTimeout(poll, 2000);
        }
      } catch (cause) {
        if (active) setError(cause instanceof Error ? cause.message : "This vend could not be found.");
      }
    };
    void poll();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [jobId]);

  if (error) return <MessagePage title="Vend unavailable" body={error} />;
  if (!job) return <LoadingScreen label="Reading the vend ticket" compact />;

  const stage = job.status === "succeeded" ? 4 : job.status === "processing" ? 3 : job.status === "queued" ? 2 : 1;
  const terminalFailure = job.status === "failed" || job.status === "refunded";

  return (
    <section className="job-page">
      <div className="job-heading">
        <p className="eyebrow">Vend ticket {job.id.slice(-8).toUpperCase()}</p>
        <h1>{job.status === "succeeded" ? "Your change is ready." : terminalFailure ? "This vend stopped safely." : "The machine is working."}</h1>
        <p>{job.promptSummary}</p>
      </div>

      <ol className="job-steps">
        {["Payment confirmed", "Queued safely", "Generating video", "Delivered"].map((label, index) => (
          <li key={label} className={stage > index ? "complete" : stage === index ? "current" : ""}>
            <span>{stage > index ? "✓" : String(index + 1).padStart(2, "0")}</span>{label}
          </li>
        ))}
      </ol>

      {job.status === "succeeded" ? (
        <div className="result-grid">
          <figure><video src={job.inputUrl} controls playsInline /><figcaption>Original</figcaption></figure>
          <figure><video src={job.resultUrl} controls playsInline /><figcaption>One Change</figcaption></figure>
        </div>
      ) : terminalFailure ? (
        <div className="failure-card">
          <strong>No delivery charge was kept.</strong>
          <p>{job.errorMessage ?? "The provider could not complete this edit. Guest payments are refunded and account credits are restored automatically."}</p>
        </div>
      ) : (
        <div className="working-card">
          <div className="scanline" />
          <span>Gemini Omni Flash</span>
          <strong>Generating one complete video</strong>
          <small>You can leave this page and return with the same browser.</small>
        </div>
      )}

      <div className="job-actions">
        {job.downloadUrl ? <a href={job.downloadUrl} download>Download MP4</a> : null}
        <Link to="/">Start another vend</Link>
      </div>
    </section>
  );
}

function MachinePage() {
  const [status, setStatus] = useState<MachinePublicStatus | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    apiGet<{ machine: MachinePublicStatus }>("/api/machine")
      .then(({ machine }) => setStatus(machine))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Machine status is unavailable."));
  }, []);

  if (error) return <MessagePage title="Status unavailable" body={error} />;
  if (!status) return <LoadingScreen label="Reading the public Machine record" compact />;

  const claims = [
    ["Available test cash", status.pool.availableMinor],
    ["Customer obligations", -status.pool.obligationsMinor],
    ["Unspent credits", -status.pool.creditLiabilityMinor],
    ["Active commitments", -status.pool.commitmentsMinor],
    ["Incurred costs", -status.pool.costsMinor],
    ["Prior charity allocations", -status.pool.charityAllocationMinor],
    ["Bounded reserve", -status.pool.reserveMinor],
  ] as const;

  return (
    <section className="machine-page">
      <div className="machine-title-row">
        <div>
          <p className="eyebrow">Public operating record</p>
          <h1>The business<br />runs itself.</h1>
        </div>
        <div className={`machine-state ${status.intake}`}>
          <span />
          <strong>{status.testMode ? "TEST MODE" : status.mode.toUpperCase()}</strong>
          <small>Intake {status.intake} · provider {status.provider}</small>
        </div>
      </div>

      <div className="pool-panel">
        <div className="pool-total">
          <span>Distributable overflow</span>
          <strong>{money(status.pool.distributableMinor)}</strong>
          <small>100% charity-restricted after obligations, costs, and reserve.</small>
        </div>
        <div className="pool-lines">
          {claims.map(([label, value]) => (
            <div key={label}><span>{label}</span><strong className={value < 0 ? "negative" : ""}>{value < 0 ? "−" : ""}{money(Math.abs(value))}</strong></div>
          ))}
        </div>
      </div>

      <div className="machine-columns">
        <article>
          <p className="step-label"><span>RULES</span> Fixed and public</p>
          <h2>No agent required.</h2>
          <p>
            One current snapshot enters a deterministic policy. The host then receives
            only named, revision-bound actions. Ordinary checkout and video generation
            never depend on this loop.
          </p>
          <ul>{status.boundary.map((item) => <li key={item}>{item}</li>)}</ul>
          <dl>
            <div><dt>Policy cycle</dt><dd>Every {status.profile.runEvery}</dd></div>
            <div><dt>Snapshot limit</dt><dd>{status.profile.snapshotMaxAge}</dd></div>
            <div><dt>Reserve</dt><dd>{status.profile.reserveRunwayDays}d / {status.profile.reserveLookbackDays}d lookback</dd></div>
            <div><dt>Source</dt><dd>{status.profile.sourcePublished ? <a href={status.profile.source}>GitHub</a> : "Pending publication"}</dd></div>
          </dl>
        </article>
        <article>
          <p className="step-label"><span>CHARITY</span> Overflow destination</p>
          <h2>{status.charity.destination}</h2>
          <p>
            The prototype records test allocations only. A real destination and money
            rail activate with live payments; the surplus rule itself does not change.
          </p>
          <dl>
            <div><dt>Next close</dt><dd>{new Date(status.charity.nextCloseAt).toLocaleDateString(undefined, { timeZone: "UTC" })}</dd></div>
            <div><dt>Close cadence</dt><dd>{status.profile.overflowCloseEvery}</dd></div>
            <div><dt>Allocated</dt><dd>{money(status.charity.totalAllocatedMinor)}</dd></div>
          </dl>
        </article>
      </div>

      <section className="history-panel">
        <div><p className="eyebrow">Recent decisions</p><h2>Receipts, not promises.</h2></div>
        <ol>
          {status.recentHistory.length ? status.recentHistory.map((entry) => (
            <li key={entry.id}>
              <time>{new Date(entry.at).toLocaleString()}</time>
              <span>{entry.kind}</span>
              <strong>{entry.summary}</strong>
              <em>{entry.status}</em>
            </li>
          )) : <li className="empty-history">The first scheduled cycle will appear here.</li>}
        </ol>
      </section>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <span>ONE CHANGE / A MACHINE</span>
      <p>Generative edits are best-effort. No pixel-exact preservation is promised.</p>
      <Link to="/machine">View public economics</Link>
    </footer>
  );
}

function LoadingScreen({ label, compact = false }: { label: string; compact?: boolean }) {
  return <div className={`loading-screen ${compact ? "compact" : ""}`}><span /><p>{label}</p></div>;
}

function MessagePage({ title, body }: { title: string; body: string }) {
  return <section className="message-page"><p className="eyebrow">One Change</p><h1>{title}</h1><p>{body}</p><Link to="/">Return home</Link></section>;
}

function readVideoDuration(url: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const value = video.duration;
      video.removeAttribute("src");
      video.load();
      if (!Number.isFinite(value)) reject(new Error("That video has no readable duration."));
      else resolve(value);
    };
    video.onerror = () => reject(new Error("That MP4 could not be read by this browser."));
    video.src = url;
  });
}
